import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Ballot, Inbox, Unarchive } from '@material-ui/icons';
import {
    ClickAwayListener,
    makeStyles,
    MenuItem as MuiMenuItem,
    MenuList,
} from '@material-ui/core';
import { Button } from '@avangard/ui/core';

import { PopperPopup } from '@modules/layout/moleculas';
import { StudentsTemplateFormModal, StudentsImportFormModal } from '@modules/students/organisms';

type ButtonStudentsImportProps = {
    canImport: boolean;
    canDownloadTemplate: boolean;
};

const useButtonStyles = makeStyles({
    root: {
        minWidth: 32,
        width: 32,
        flex: '0 0 auto',
    },
    startIcon: {
        margin: 0,
    },
});

const AwayListenerRoot = styled.div``;

const StyledPopperPopup = styled(PopperPopup)`
    margin-top: 8px;
    z-index: 100;
`;

const MenuItem = styled(MuiMenuItem)`
    > span:first-child {
        display: flex;
        width: 18px;
        height: 18px;
    }

    > p {
        width: calc(100% - 32px);
        margin-inline-start: 8px;
    }
`;

const unarchiveIcon = <Unarchive style={{ fontSize: 18 }} />;
const ballotIcon = <Ballot style={{ fontSize: 18 }} />;
const inboxIcon = <Inbox style={{ fontSize: 18 }} />;

const ButtonStudentsImport = (props: ButtonStudentsImportProps): React.ReactElement => {
    const { canImport, canDownloadTemplate } = props;

    const { t } = useTranslation(['students']);

    const buttonClasses = useButtonStyles();

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handlePopperClose = (): void => setAnchorEl(null);
    const handleClickPopperToggle = (event: React.SyntheticEvent<HTMLElement>): void =>
        setAnchorEl(anchorEl ? null : event.currentTarget);

    const [openStudentsImportModal, setStudentsImportModal] = React.useState<boolean>(false);

    const handleOpenStudentsImportModal = (): void => setStudentsImportModal(true);
    const handleCloseStudentsImportModal = (): void => setStudentsImportModal(false);

    const [openStudentsTemplateFormModal, setStudentsTemplateFormModal] =
        React.useState<boolean>(false);

    const handleOpenStudentsTemplateFormModal = (): void => setStudentsTemplateFormModal(true);
    const handleCloseStudentsTemplateFormModal = (): void => setStudentsTemplateFormModal(false);

    return (
        <>
            <ClickAwayListener onClickAway={handlePopperClose}>
                <AwayListenerRoot>
                    <Button
                        classes={buttonClasses}
                        variant='outlined'
                        onClick={handleClickPopperToggle}
                        startIcon={inboxIcon}
                    />

                    <StyledPopperPopup
                        placement='bottom-start'
                        open={!!anchorEl}
                        anchorEl={anchorEl}
                    >
                        <MenuList>
                            {canImport ? (
                                <MenuItem onClick={handleOpenStudentsImportModal}>
                                    <span>{unarchiveIcon}</span>
                                    <p>{t('students:actions.students_import')}</p>
                                </MenuItem>
                            ) : null}

                            {canDownloadTemplate ? (
                                <MenuItem onClick={handleOpenStudentsTemplateFormModal}>
                                    <span>{ballotIcon}</span>
                                    <p>{t('students:actions.students_template_export')}</p>
                                </MenuItem>
                            ) : null}
                        </MenuList>
                    </StyledPopperPopup>
                </AwayListenerRoot>
            </ClickAwayListener>

            <StudentsTemplateFormModal
                open={openStudentsTemplateFormModal}
                onClose={handleCloseStudentsTemplateFormModal}
            />

            <StudentsImportFormModal
                open={openStudentsImportModal}
                onClose={handleCloseStudentsImportModal}
            />
        </>
    );
};

export { ButtonStudentsImport };
